import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  Snackbar,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Backdrop,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  CameraAlt,
  Link as LinkIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DocumentViewer } from './DocumentViewer';
import { ExtractionInterface } from './ExtractionInterface';
import { useAIProcessing } from '../services/aiProcessingService';
import { useDropzone } from 'react-dropzone';
import { db } from '../services/database';

export const DocumentProcessor: React.FC = () => {
  const [currentDocument, setCurrentDocument] = useState<{
    id: string;
    name: string;
    file?: File;
    imageData?: ImageData;
  } | null>(null);
  
  const [extractionResult, setExtractionResult] = useState<any>(null);
  const [selectedRegion, setSelectedRegion] = useState<number | undefined>();
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  const { processDocument, processing, status } = useAIProcessing();

  // File drop handler
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    const documentId = crypto.randomUUID();

    // Save to database
    try {
      const docType = file.type.startsWith('image/') ? 'image' : 
                      file.type === 'application/pdf' ? 'pdf' : 
                      file.type.startsWith('audio/') ? 'audio' : 'image';
      
      const fileData = await file.arrayBuffer();
      
      await db.saveDocument({
        id: documentId,
        name: file.name,
        type: docType,
        size: file.size,
        data: fileData,
        uploadedAt: new Date(),
        lastModified: new Date(file.lastModified),
        tags: [],
        projectId: undefined,
      });

      setCurrentDocument({
        id: documentId,
        name: file.name,
        file,
      });

      setSnackbar({
        open: true,
        message: `Document "${file.name}" uploaded successfully`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Document save error:', error);
      setSnackbar({
        open: true,
        message: `Failed to save document: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error',
      });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
  });

  const handleImageReady = useCallback(
    async (imageData: ImageData) => {
      if (!currentDocument || isProcessing) return;

      // Prevent multiple processing calls
      setIsProcessing(true);

      // Automatically start processing
      try {
        const result = await processDocument(currentDocument.id, imageData, {
          useGemma: true,
          useTesseract: true,
          preprocessOptions: {
            enhance: true,
          },
        });

        setExtractionResult(result);
        
        // Update document as processed
        await db.updateDocument(currentDocument.id, {
          lastModified: new Date(),
        });

        setSnackbar({
          open: true,
          message: 'Document processed successfully',
          severity: 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Processing failed',
          severity: 'error',
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [currentDocument, processDocument, isProcessing]
  );

  const handleCorrection = useCallback(
    async (field: string, oldValue: string, newValue: string) => {
      if (!extractionResult) return;

      // Save correction to database
      try {
        await db.addCorrection(extractionResult.id, {
          field,
          oldValue,
          newValue,
        });

        // Update local state
        // This is simplified - in production, you'd update the nested field properly
        setExtractionResult({
          ...extractionResult,
          corrections: [
            ...(extractionResult.corrections || []),
            { field, oldValue, newValue },
          ],
        });

        setSnackbar({
          open: true,
          message: 'Correction saved',
          severity: 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to save correction',
          severity: 'error',
        });
      }
    },
    [extractionResult]
  );

  const handleReprocess = useCallback(
    async (options: any) => {
      if (!currentDocument || !currentDocument.imageData) return;

      const result = await processDocument(
        currentDocument.id,
        currentDocument.imageData,
        options
      );

      setExtractionResult(result);
    },
    [currentDocument, processDocument]
  );

  const handleUploadClick = () => {
    document.getElementById('file-input')?.click();
  };

  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Implementation for camera capture would go here
      stream.getTracks().forEach(track => track.stop());
      setSnackbar({
        open: true,
        message: 'Camera capture not yet implemented',
        severity: 'info',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Camera access denied',
        severity: 'error',
      });
    }
  };

  const handleUrlUpload = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      // Implementation for URL upload would go here
      setSnackbar({
        open: true,
        message: 'URL upload not yet implemented',
        severity: 'info',
      });
    }
  };

  const speedDialActions = [
    { icon: <InsertDriveFile />, name: 'Upload File', action: handleUploadClick },
    { icon: <CameraAlt />, name: 'Camera Capture', action: handleCameraCapture },
    { icon: <LinkIcon />, name: 'From URL', action: handleUrlUpload },
  ];

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 2, zIndex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5">Document Processing</Typography>
          {currentDocument && (
            <>
              <Typography color="text.secondary">|</Typography>
              <Typography variant="subtitle1">{currentDocument.name}</Typography>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {status && (
            <Alert severity="info" sx={{ py: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">
                  Workers: {status.workers.activeWorkers}/{status.workers.totalWorkers}
                </Typography>
                {status.workers.queueLength > 0 && (
                  <Typography variant="body2">
                    Queue: {status.workers.queueLength}
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}
        </Stack>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
        {!currentDocument ? (
          // Upload Area
          <Paper
            {...getRootProps()}
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <input {...getInputProps()} id="file-input" />
            <Stack spacing={2} alignItems="center">
              <CloudUpload sx={{ fontSize: 64, color: 'action.disabled' }} />
              <Typography variant="h6" color="text.secondary">
                {isDragActive
                  ? 'Drop the document here'
                  : 'Drag & drop a document here, or click to select'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supports images (PNG, JPG, etc.) and PDFs
              </Typography>
            </Stack>
          </Paper>
        ) : (
          // Document View
          <Box sx={{ display: 'flex', gap: 2, height: '100%', flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1, height: { xs: '50%', md: '100%' } }}>
              <DocumentViewer
                documentId={currentDocument.id}
                file={currentDocument.file}
                imageData={currentDocument.imageData}
                onImageReady={(imageData) => {
                  setCurrentDocument({ ...currentDocument, imageData });
                  handleImageReady(imageData);
                }}
                extractedRegions={
                  extractionResult?.tesseractResult?.blocks.map((block: any) => ({
                    bbox: block.bbox,
                    text: block.text,
                    confidence: block.confidence,
                    type: 'text',
                  })) || []
                }
                selectedRegion={selectedRegion}
                onRegionClick={setSelectedRegion}
              />
            </Box>
            <Box sx={{ flex: 1, height: { xs: '50%', md: '100%' }, overflow: 'auto' }}>
              <ExtractionInterface
                extractionResult={extractionResult}
                onCorrection={handleCorrection}
                onReprocess={handleReprocess}
                loading={processing}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Speed Dial for new document */}
      {currentDocument && (
        <SpeedDial
          ariaLabel="Upload options"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          icon={<SpeedDialIcon />}
          onClose={() => setSpeedDialOpen(false)}
          onOpen={() => setSpeedDialOpen(true)}
          open={speedDialOpen}
        >
          {speedDialActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={() => {
                setSpeedDialOpen(false);
                action.action();
              }}
            />
          ))}
        </SpeedDial>
      )}

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={processing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setSnackbar({ ...snackbar, open: false })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};